import { useEffect, useState } from "react";
import { Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const shareUrl = (token) => `${window.location.origin}/s/${token}`;
export const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copied"); };

export default function ShareButton({ kind, refId, label = "Share", disabled }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ expiry_days: "", password: "" });
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/shares/config").then((r) => { setCfg(r.data); setF((x) => ({ ...x, expiry_days: r.data.default_expiry_days })); }).catch(() => {}); }, []);
  if (!cfg?.enabled || !cfg.can_share) return null;
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/shares", { kind, ref_id: refId || null, expiry_days: Number(f.expiry_days) || null, password: f.password || null });
      setLink(shareUrl(r.data.share_token));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => { setOpen(false); setLink(null); setF({ expiry_days: cfg.default_expiry_days, password: "" }); };
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} disabled={disabled} data-testid={`share-button-${kind}`}><Share2 className="h-4 w-4 mr-2" />{label}</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-white border-slate-800" data-testid="share-dialog">
          <DialogHeader><DialogTitle>Create Smart Share Link</DialogTitle><DialogDescription>Anyone with the link can view this report read-only, with no login needed. You can revoke it anytime.</DialogDescription></DialogHeader>
          {!link ? (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs">Expires in (days)</Label><Input type="number" min={1} max={365} value={f.expiry_days} onChange={(e) => setF({ ...f, expiry_days: e.target.value })} data-testid="share-expiry-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Password (optional)</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Leave blank for no password" data-testid="share-password-input" /></div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Share link</Label>
              <div className="flex gap-2"><Input readOnly value={link} data-testid="share-link-output" /><Button onClick={() => copy(link)} data-testid="share-copy-button"><Copy className="h-4 w-4" /></Button></div>
              <p className="text-xs text-slate-500">Track views or revoke it from Organization → Shared Links.</p>
            </div>
          )}
          <DialogFooter>{!link ? <Button onClick={create} disabled={busy} data-testid="share-create-button">{busy ? "Creating..." : "Create link"}</Button> : <Button variant="secondary" onClick={close}>Done</Button>}</DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
